import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { takePendingMobileFeedRestoreScrollY } from '@/src/lib/navigation/mobileFeedTransitionScroll';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

type ScrollOwner = { kind: 'window' } | { kind: 'element'; element: HTMLElement };

type ElementLockSnapshot = {
  overflowY: string;
  overscrollBehavior: string;
};

function getScrollOwner(preferWindow = false): ScrollOwner {
  if (preferWindow) return { kind: 'window' };
  if (typeof document === 'undefined') return { kind: 'window' };

  if ((window.scrollY || window.pageYOffset || 0) > 1) {
    return { kind: 'window' };
  }

  const element = document.querySelector<HTMLElement>('[data-search-content-panel]');
  if (element) {
    return { kind: 'element', element };
  }

  return { kind: 'window' };
}

function getScrollPosition(owner: ScrollOwner): number {
  if (owner.kind === 'element') {
    return owner.element.scrollTop;
  }

  return window.scrollY || window.pageYOffset || 0;
}

function setScrollPosition(owner: ScrollOwner, y: number) {
  if (owner.kind === 'element') {
    owner.element.scrollTop = y;
    return;
  }

  window.scrollTo(0, y);
}

function lockOwnerScroll(owner: ScrollOwner): ElementLockSnapshot | null {
  if (owner.kind !== 'element') return null;

  const { style } = owner.element;
  const snapshot = {
    overflowY: style.overflowY || '',
    overscrollBehavior: style.overscrollBehavior || '',
  };

  style.overflowY = 'hidden';
  style.overscrollBehavior = 'none';

  return snapshot;
}

function restoreOwnerScrollLock(owner: ScrollOwner, snapshot: ElementLockSnapshot | null) {
  if (owner.kind !== 'element' || !snapshot) return;

  const { style } = owner.element;

  if (snapshot.overflowY) {
    style.overflowY = snapshot.overflowY;
  } else {
    style.removeProperty('overflow-y');
  }

  if (snapshot.overscrollBehavior) {
    style.overscrollBehavior = snapshot.overscrollBehavior;
  } else {
    style.removeProperty('overscroll-behavior');
  }
}

function scheduleScroll(owner: ScrollOwner, y: number, onSettled?: () => void): () => void {
  if (typeof window === 'undefined') return () => {};

  let frame = 0;
  let attempts = 0;
  let cancelled = false;

  const tick = () => {
    if (cancelled) return;

    setScrollPosition(owner, y);
    attempts += 1;

    const currentY = getScrollPosition(owner);
    if (Math.abs(currentY - y) <= 1 || attempts >= 12) {
      onSettled?.();
      return;
    }

    frame = window.requestAnimationFrame(tick);
  };

  tick();

  return () => {
    cancelled = true;
    if (frame) window.cancelAnimationFrame(frame);
  };
}

/**
 * Feed mode should always start at the top of the viewport, regardless of the
 * list scroll position it was launched from. When exiting feed mode, we
 * restore the prior list scroll so "Back to list" returns the user to where
 * they were browsing.
 */
export function useMobileFeedTransitionScroll(enabled: boolean): boolean {
  const [isScrollSettled, setIsScrollSettled] = useState(!enabled);
  const wasEnabledRef = useRef(false);
  const restoreScrollYRef = useRef(0);
  const scrollOwnerRef = useRef<ScrollOwner | null>(null);
  const ownerLockSnapshotRef = useRef<ElementLockSnapshot | null>(null);
  const cancelScheduledScrollRef = useRef<(() => void) | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    cancelScheduledScrollRef.current?.();
    cancelScheduledScrollRef.current = null;

    const wasEnabled = wasEnabledRef.current;

    if (!wasEnabled && enabled) {
      const pendingRestoreScrollY = takePendingMobileFeedRestoreScrollY();
      const owner = getScrollOwner(pendingRestoreScrollY != null);
      const scrollY = pendingRestoreScrollY ?? getScrollPosition(owner);
      const currentScrollY = getScrollPosition(owner);
      scrollOwnerRef.current = owner;
      restoreScrollYRef.current = scrollY;
      ownerLockSnapshotRef.current = lockOwnerScroll(owner);
      setIsScrollSettled(currentScrollY <= 1);
      cancelScheduledScrollRef.current = scheduleScroll(owner, 0, () => {
        setIsScrollSettled(true);
      });
    } else if (wasEnabled && !enabled) {
      const owner = scrollOwnerRef.current ?? getScrollOwner();
      setIsScrollSettled(true);
      restoreOwnerScrollLock(owner, ownerLockSnapshotRef.current);
      ownerLockSnapshotRef.current = null;
      cancelScheduledScrollRef.current = scheduleScroll(owner, restoreScrollYRef.current);
    } else if (!enabled) {
      setIsScrollSettled(true);
    }

    wasEnabledRef.current = enabled;

    return () => {
      cancelScheduledScrollRef.current?.();
      cancelScheduledScrollRef.current = null;

      if (enabled) {
        const owner = scrollOwnerRef.current;
        if (owner) {
          restoreOwnerScrollLock(owner, ownerLockSnapshotRef.current);
        }
        ownerLockSnapshotRef.current = null;
      }
    };
  }, [enabled]);

  return isScrollSettled;
}
